import React, { useState, useEffect, useMemo, useRef } from 'react';
import { CRow, CCol } from '@coreui/react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { listBlogs } from 'src/actions/blogActions';
import { fetchBlogMetrics } from 'src/actions/blogMetricsActions';
import { imgUrl } from 'src/actions/types';

const useDraggableScroll = (ref) => {
  useEffect(() => {
    const element = ref.current;
    if (!element) return;
    let isDown = false, startX, scrollLeft;
    element.style.cursor = 'grab';
    const onDown = (e) => { isDown = true; element.classList.add('active-dragging'); element.style.cursor = 'grabbing'; element.style.userSelect = 'none'; startX = e.pageX - element.offsetLeft; scrollLeft = element.scrollLeft; e.preventDefault(); };
    const onLeave = () => { element.style.cursor = 'grab'; };
    const onUp = () => { isDown = false; element.classList.remove('active-dragging'); element.style.cursor = 'grab'; element.style.userSelect = ''; };
    const onMove = (e) => { if (!isDown) return; e.preventDefault(); element.scrollLeft = scrollLeft - (e.pageX - element.offsetLeft - startX) * 2; };
    element.addEventListener('mousedown', onDown);
    element.addEventListener('mouseleave', onLeave);
    element.addEventListener('mouseup', onUp);
    element.addEventListener('mousemove', onMove);
    return () => {
      element.removeEventListener('mousedown', onDown);
      element.removeEventListener('mouseleave', onLeave);
      element.removeEventListener('mouseup', onUp);
      element.removeEventListener('mousemove', onMove);
    };
  }, [ref]);
};

const BlogSection = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const blogScrollRef = useRef(null);

  const blogs = useSelector(state => state.blogReducer.blogs || []);
  const doctors = useSelector(state => state.doctorList?.doctors || []);
  const metrics = useSelector(state => state.blogMetricsReducer.metrics || {});

  useDraggableScroll(blogScrollRef);

  useEffect(() => {
    dispatch(listBlogs());
  }, [dispatch]);

  useEffect(() => {
    if (blogs.length > 0) {
      dispatch(fetchBlogMetrics());
    }
  }, [blogs, dispatch]);

  const mergedBlogs = useMemo(() => {
    if (!blogs || !doctors) return [];
    return blogs
      .filter(blog => blog.status === 'published')
      .map(blog => {
        const isAdmin = blog.hospitalID === 0;
        const doctor = isAdmin ? null : doctors.find(d => d.hospitalID === blog.hospitalID);
        const metric = metrics?.[blog.id] || {};
        return {
          ...blog,
          doctorName: doctor
            ? `Dr. ${doctor.firstName} ${doctor.lastName}`
            : isAdmin ? 'Admin' : 'Unknown',
          doctorImage: doctor
            ? doctor.uploadPhoto ? `${imgUrl}${doctor.uploadPhoto}` : '/images/default-doctor.png'
            : isAdmin ? '/images/admin.png' : '/images/default.png',
          views: metric.uniqueViewersCount || 0,
          likes: metric.uniqueLikesCount || 0,
        };
      });
  }, [blogs, doctors, metrics]);

  const sortedBlogs = useMemo(() => {
    return mergedBlogs
      .slice()
      .sort((a, b) => {
        if (b.views === a.views) return b.likes - a.likes;
        return b.views - a.views;
      })
      .slice(0, 5);
  }, [mergedBlogs]);

  return (
    <>
      <style>{`
        .blog-section-wrapper { margin-bottom: 2rem; }

        .blog-scroll-row-section {
          display: flex;
          flex-direction: row;
          flex-wrap: nowrap;
          overflow-x: auto;
          gap: 15px;
          padding: 10px;
          scrollbar-width: none;
          -ms-overflow-style: none;
          -webkit-overflow-scrolling: touch;
          cursor: grab;
          user-select: none;
        }
        .blog-scroll-row-section::-webkit-scrollbar { display: none !important; }
        .blog-scroll-row-section.active-dragging { cursor: grabbing !important; }

        .blog-card-section {
          width: 260px;
          min-width: 260px;
          height: 320px;
          border-radius: 10px;
          box-shadow: 0 1px 4px rgba(0,0,0,0.1);
          background: #fff;
          border: 1px solid #e0e0e0;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          flex: 0 0 auto;
          cursor: pointer;
          transition: box-shadow 0.3s ease, transform 0.3s ease;
        }
        .blog-card-section:hover {
          box-shadow: 0 8px 24px rgba(0,0,0,0.15);
          transform: translateY(-6px);
        }

        .blog-card-section-img {
          width: 100%;
          height: 120px;
          object-fit: cover;
          border-top-left-radius: 10px;
          border-top-right-radius: 10px;
          background-color: #f9f9f9;
          pointer-events: none;
        }

        .blog-card-section-body {
          padding: 12px 12px 8px 12px;
          display: flex;
          flex-direction: column;
          flex: 1;
          min-height: 0;
          justify-content: space-between;
          overflow: hidden;
        }

        .blog-card-section-title {
          font-weight: 600;
          font-size: 15px;
          margin-bottom: 2px;
          color: #1a1a1a;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          pointer-events: none;
        }

        .blog-card-section-desc {
          font-size: 12.7px;
          color: #444;
          line-height: 1.45;
          overflow: hidden;
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          min-height: 35px;
          margin-bottom: 8px;
          pointer-events: none;
        }

        .blog-card-section-author {
          display: flex;
          align-items: center;
          font-size: 12px;
          color: #444;
          gap: 6px;
          margin: 2px 0 6px 0;
          cursor: pointer;
        }

        .blog-card-section-avatar {
          width: 22px;
          height: 22px;
          border-radius: 50%;
          margin-right: 4px;
          object-fit: cover;
          border: 1px solid #eee;
          pointer-events: none;
        }

        .blog-card-section-author-name {
          font-weight: 500;
          max-width: 100px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          pointer-events: none;
        }

        .blog-card-section-stats {
          display: inline-flex;
          align-items: center;
          pointer-events: none;
        }

        @media (max-width: 600px) {
          .blog-card-section {
            width: 97%;
            min-width: 97%;
          }
        }
      `}</style>

      <section className="container blog-section-wrapper">
        <CRow className="align-items-center">
          {/* Left Header */}
          <CCol xl={3} lg={4} md={12}>
            <div className="section-header">
              <h2>Read top articles from health experts</h2>
              <p>
                Health articles that keep you informed about good practices
                and achieve your goals.
              </p>
              <button
                className="view-button"
                onClick={() => history.push('/doctors-blog')}
              >
                Explore All Articles
              </button>
            </div>
          </CCol>

          {/* Right Scrollable Blog Cards */}
          <CCol xl={9} lg={8} md={12}>
            <div
              ref={blogScrollRef}
              className="blog-scroll-row-section"
            >
              {sortedBlogs.length > 0 ? (
                sortedBlogs.map(blog => (
                  <div
                    key={blog.id}
                    className="blog-card-section"
                    onClick={() => history.push(`/doctors-blog/${blog.id}`)}
                  >
                    {/* Blog Image */}
                    {blog.image && (
                      <img
                        src={`${imgUrl}${blog.image}`}
                        alt={blog.title}
                        draggable="false"
                        className="blog-card-section-img"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    )}

                    {/* Blog Body */}
                    <div className="blog-card-section-body">
                      {/* Title */}
                      <div className="blog-card-section-title" title={blog.title}>
                        {blog.title}
                      </div>

                      {/* Description */}
                      <div
                        className="blog-card-section-desc"
                        dangerouslySetInnerHTML={{
                          __html: blog.content && blog.content !== '<p><br></p>'
                            ? blog.content
                            : 'No description available.'
                        }}
                      />

                      {/* Author Row */}
                      <div
                        className="blog-card-section-author"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (blog.doctorName !== 'Admin') {
                            history.push(`/Doctorprofile/${blog.hospitalID}`);
                          }
                        }}
                      >
                        {blog.doctorImage && blog.doctorName !== 'Admin' ? (
                          <img
                            src={blog.doctorImage}
                            alt={blog.doctorName}
                            draggable="false"
                            className="blog-card-section-avatar"
                          />
                        ) : (
                          <i className="fas fa-user-circle"
                            style={{ fontSize: 19, marginRight: 5, color: '#c0c0c0', pointerEvents: 'none' }}
                          />
                        )}
                        <span className="blog-card-section-author-name">
                          {blog.doctorName || 'Unknown'}
                        </span>
                        <span className="blog-card-section-stats" style={{ marginLeft: 8 }}>
                          <i className="fas fa-thumbs-up" style={{ color: '#f0ad4e', marginRight: 2 }}></i>
                          {blog.likes || 0}
                        </span>
                        <span className="blog-card-section-stats" style={{ marginLeft: 5 }}>
                          <i className="fas fa-eye" style={{ color: '#5bc0de', marginRight: 2 }}></i>
                          {blog.views || 0}
                        </span>
                      </div>

                      {/* See More Button */}
                      <button
                        className="view-button"
                        style={{ width: '100%', fontSize: 13, marginTop: 0 }}
                        onClick={(e) => {
                          e.stopPropagation();
                          history.push(`/doctors-blog/${blog.id}`);
                        }}
                      >
                        See More
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center w-100 py-4">
                  <p className="text-muted">No articles available at the moment.</p>
                </div>
              )}
            </div>
          </CCol>
        </CRow>
      </section>
    </>
  );
};

export default BlogSection;